import { useEffect, useState } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { worldToScreen } from '../canvas/viewport';
import { Blob } from '../render/Blob';
import { usePresenceCtx } from './PresenceProvider';
import { useReducedMotion } from './useReducedMotion';
import type { PresencePeer } from './types';

const IDLE_MS = 5000;

/**
 * Screen-space overlay of every peer's cursor, mapped through the LOCAL viewport
 * (so a peer's canvas-coord cursor lands correctly at any zoom/pan). Human peers
 * get a colored pointer + initials pill; the agent (Viv) gets a <Blob> + pulse +
 * label. Positions lerp via a CSS transition (snapped under reduced-motion);
 * cursors idle-fade after 5s of no movement.
 */
export function PresenceCursors() {
  const { others } = usePresenceCtx();
  const viewport = useCanvasStore((s) => s.doc.viewport);
  const reduced = useReducedMotion();

  // Re-evaluate idle fade roughly once a second.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const withCursor = others.filter((p) => p.cursor);
  if (withCursor.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[55]" aria-hidden>
      {withCursor.map((p) => (
        <PeerCursor key={p.id} peer={p} screen={worldToScreen(p.cursor!, viewport)} reduced={reduced} />
      ))}
    </div>
  );
}

function PeerCursor({ peer, screen, reduced }: { peer: PresencePeer; screen: { x: number; y: number }; reduced: boolean }) {
  // Track last-move time to drive the idle fade.
  const [lastMove, setLastMove] = useState(() => Date.now());
  const key = `${Math.round(screen.x)},${Math.round(screen.y)}`;
  useEffect(() => { setLastMove(Date.now()); }, [key]);
  const idle = Date.now() - lastMove > IDLE_MS;

  return (
    <div
      data-presence-cursor
      className="absolute left-0 top-0 will-change-transform"
      style={{
        transform: `translate3d(${screen.x}px, ${screen.y}px, 0)`,
        transition: reduced ? 'none' : 'transform 80ms linear, opacity 600ms ease',
        opacity: idle ? 0 : 1,
      }}
    >
      {peer.isAgent ? <AgentCursor peer={peer} reduced={reduced} /> : <HumanCursor peer={peer} />}
    </div>
  );
}

// §22 cursor anatomy: pointer + speech-bubble initials/name pill.
function HumanCursor({ peer }: { peer: PresencePeer }) {
  return (
    <div className="relative">
      <svg width="22" height="22" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.4))', display: 'block' }}>
        <path d="M5 2.5l13.5 7-6.2 1.4L9 18z" fill={peer.color} stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" />
      </svg>
      <span
        className="absolute left-[15px] top-[17px] whitespace-nowrap px-2 py-0.5 font-mono text-[12px] font-semibold text-white"
        style={{ background: peer.color, borderRadius: '7px 7px 7px 1px', boxShadow: '0 3px 10px -3px rgba(0,0,0,.45)' }}
      >
        {peer.name}
      </span>
    </div>
  );
}

function AgentCursor({ peer, reduced }: { peer: PresencePeer; reduced: boolean }) {
  return (
    <div className="relative">
      {!reduced && (
        <span
          className="absolute -left-[7px] -top-[7px] h-[34px] w-[34px] rounded-full"
          style={{ border: '2px solid var(--color-accent)', animation: 'cf-presence-pulse 1.7s var(--ease-out, ease-out) infinite' }}
        />
      )}
      <Blob size={20} />
      <span
        className="absolute left-[18px] top-[17px] whitespace-nowrap px-2 py-0.5 font-mono text-[12px] font-semibold text-white"
        style={{ background: 'var(--grad)', borderRadius: '7px 7px 7px 1px', boxShadow: '0 3px 10px -3px rgba(0,0,0,.45)' }}
      >
        {peer.name}
      </span>
    </div>
  );
}
